
import Widget from "components/widget/Widget";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useState } from "react";
import { FaUser, FaLink, FaKey } from "react-icons/fa";
import { FaGamepad } from "react-icons/fa6";
import { MdFiberNew, MdVerifiedUser, MdDateRange } from "react-icons/md";
import { DiGoogleAnalytics } from "react-icons/di";
import { TiArrowBack } from "react-icons/ti";
import Datepicker from "react-tailwindcss-datepicker";
import { partnerDashboardAPI } from "server";
import { convertDateTime, convertUTCAfter, convertUTCBefore } from "utils/utils";
import ColumnsTable from "./components/ColumnsTable";
import TrafficsCATGMChart from "./components/TrafficsCATGMChart";
import TrafficsPartnerChart from "./components/TrafficsPartnerChart";
import { catgmTrafficsColumns } from "./variables/catgmTrafficsColumns";
import { partnerTrafficsColumns } from "./variables/partnerTrafficsColumns";
import { HiMiniCurrencyDollar, HiUserCircle } from "react-icons/hi2";
import Card from "components/card";
import { CustomFlowbiteTheme, Flowbite, Tabs } from "flowbite-react";
import { MdTaskAlt } from "react-icons/md";
import { partnerLogType } from "variables/refType";
import NoData from "components/nodata";
import SplineChart from "./components/SplineChart";
import TotalLifetime from "./components/TotalLifetime";


const Dashboard = () => {
  const [partner, setPartner] = useState(JSON.parse(sessionStorage.getItem("admin")))
  const [profile, setProfile] = useState<any>()
  const [report, setReport] = useState<any>()
  const [groupData, setGroupData] = useState<any>()
  const [paymentTotal, setPaymentTotal] = useState<any>()
  const [partnerTraffics, setPartnerTraffics] = useState<any>()
  const [catgmTraffics, setCatgmTraffics] = useState<any>()
  const [trafficsData, setTrafficsData] = useState<any>()


  const [value, setValue] = useState<any>({
    startDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });

  const dateQuery = useDebounce(value, 1000)
  const handleValueChange = (newValue: any) => {
    if (newValue?.startDate && newValue?.endDate) {
      setValue({
        startDate: new Date(newValue?.startDate),
        endDate: new Date(newValue?.endDate)
      });
    }
  };

  const getUserPaymentTotal = async () => {
    if (partner && partner?.partnerID) {
      const res: any = await partnerDashboardAPI.getUserPaymentTotal(partner.partnerID)
      if (res?.code === 0) {
        setPaymentTotal(res.data)
      }
    }
  }

  const getProfile = async () => {
    if (partner && partner?.partnerID) {
      const res: any = await partnerDashboardAPI.getProfile(partner.partnerID)
      if (res?.code === 0) {
        setProfile(res.data)
      }
    }
  }

  const getTrackingReport = async () => {
    if (partner && partner?.partnerID) {
      const res: any = await partnerDashboardAPI.getTrackingReport(convertUTCBefore(value.startDate), convertUTCAfter(value.endDate))
      if (res?.code === 0) {
        setReport(res.data)
      }
    }
  }

  useEffect(() => {

    const group = groupAndSortData(report)
    if (group?.length > 0) {
      const listData: any = []
      group.forEach((item: any) => {
        const partnerData = item?.dates?.map((item: any) => ({
          date: item?.date,
          questUsers: item?.data[partnerLogType.DO_QUEST]?.count || 0,
          verifiedUsers: item?.data[partnerLogType.VERIFIED_QUEST]?.count || 0,
        }))

        const catgmData = item?.dates?.map((item: any) => ({
          date: item?.date,
          totalUsers: item?.data[partnerLogType.NEW_USER]?.count + item?.data[partnerLogType.OLD_USER]?.count || 0,
          newUsers: item?.data[partnerLogType.NEW_USER]?.count || 0,
          gameUsers: item?.data[partnerLogType.GAME_USER]?.count || 0
        }))
        listData.push({ partnerData, catgmData })
      })
      setTrafficsData(listData)
    } else {
      setTrafficsData([])

    }
    setGroupData(group)


  }, [report])


  useEffect(() => {
    getTrackingReport()
  }, [dateQuery])

  useEffect(() => {
    if (partner && partner.partnerID) {
      getProfile()
      getUserPaymentTotal()
    }
  }, [partner])

  interface DataItem {
    _id: string;
    partnerID: string;
    refType: number;
    questValue: string;
    date: string;
    __v: number;
    createAt: string;
    trackingCount: number;
    updateAt: string;
  }

  interface RefTypeTotal {
    count: number;
    items: DataItem[];
  }

  interface DateData {
    date: string;
    data: Record<number, RefTypeTotal>;
  }

  interface QuestValueData {
    questValue: string;
    refTypeTotals: Record<number, number>;
    dates: DateData[];
  }

  function parseDateString(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  function groupAndSortData(data: DataItem[]): QuestValueData[] {
    if (data && data?.length > 0) {


      // First, group by questValue
      const groupedByQuestValue = data?.reduce((acc, item) => {
        if (!acc[item.questValue]) {
          acc[item.questValue] = [];
        }
        acc[item.questValue].push(item);
        return acc;
      }, {} as Record<string, DataItem[]>);

      // For each questValue, group by date and then by refType
      const result = Object.entries(groupedByQuestValue).map(([questValue, items]) => {
        // Group by date
        const groupedByDate = items.reduce((acc, item) => {
          if (!acc[item.date]) {
            acc[item.date] = [];
          }
          acc[item.date].push(item);
          return acc;
        }, {} as Record<string, DataItem[]>);

        // Sort dates
        const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
          const dateA = parseDateString(a);
          const dateB = parseDateString(b);
          return dateA.getTime() - dateB.getTime();
        });

        const refTypeTotals: Record<number, number> = {};

        // For each date, group by refType and sum trackingCount
        const datesData = sortedDates.map(date => {
          const groupedByRefType = groupedByDate[date].reduce((acc, item) => {
            if (!acc[item.refType]) {
              acc[item.refType] = {
                count: 0,
                items: []
              };
            }
            acc[item.refType].count += item.trackingCount;
            acc[item.refType].items.push(item);

            // Update total counts
            refTypeTotals[item.refType] = (refTypeTotals[item.refType] || 0) + item.trackingCount;

            return acc;
          }, {} as Record<number, RefTypeTotal>);

          return {
            date,
            data: groupedByRefType
          };
        });

        return {
          questValue,
          refTypeTotals,
          dates: datesData
        };
      });

      return result;
    }
  }

  return (
    <div>
      {/* Card widget */}
      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-3">
        {
          // profile?.apiKey &&
          <Widget
            icon={<FaKey className="h-5 w-5" />}
            title={"API Key:"}
            subtitle={profile?.apiKey ? profile?.apiKey : ""}
          />
        }
        {
          // profile?.createAt &&
          <Widget
            icon={<MdDateRange className="h-5 w-5" />}
            title={"Campaign Start Date:"}
            subtitle={profile?.createAt ? convertDateTime(profile?.createAt) : ""}
          />
        }
        {/* <Widget
          count={true}
          icon={<FaUser className="h-7 w-7" />}
          title={"Number of Paid Users"}
          subtitle={paymentTotal?.numberPaidUsers ? paymentTotal?.numberPaidUsers : 0}
        />
        <Widget
          count={true}
          icon={<HiMiniCurrencyDollar className="h-7 w-7" />}
          title={"Total Spend"}
          subtitle={`${paymentTotal?.totalSpend ? paymentTotal?.totalSpend : 0} USD`}
        /> */}
      </div>
      <div className="mt-5">
        <TotalLifetime />
      </div>
      {/* Charts */}
      <div className="my-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="col-start-1 md:col-start-2 xl:col-start-4">
          <Datepicker
            primaryColor={"indigo"}
            inputClassName=" w-full text-black dark:!text-white dark:!bg-navy-800 rounded-[10px] border-[1px] border-gray-200 shadow-md shadow-[#f3f3f35] py-4"
            showShortcuts={true}
            value={value}
            onChange={handleValueChange}
          />
        </div>
      </div>

      {
        groupData?.length > 0 ? groupData?.map((item: any, index: any) => (
          <div key={index}>
            {
              item?.questValue !== "crossQuest" && (<>
                <Card extra="rounded-[20px] p-5">
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6">
                      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-1 md:col-span-1 xl:col-span-2">
                        <Widget
                          icon={<FaLink className="h-5 w-5" />}
                          title={"Quest Link:"}
                          subtitle={item.questValue}
                        />
                        {/* <Widget
                        icon={<MdDateRange className="h-5 w-5" />}
                        title={"Start Date:"}
                        subtitle={convertDateTime(profile?.createAt)}
                      /> */}
                        <Widget
                          icon={<FaUser className="h-7 w-7" />}
                          title={"Total Quest Users"}
                          count={true}
                          subtitle={item?.refTypeTotals[partnerLogType.DO_QUEST] ? item?.refTypeTotals[partnerLogType.DO_QUEST] : 0}
                        />
                        <Widget
                          icon={<MdVerifiedUser className="h-6 w-6" />}
                          title={"Total Verified Users"}
                          count={true}
                          subtitle={item?.refTypeTotals[partnerLogType.VERIFIED_QUEST] ? item?.refTypeTotals[partnerLogType.VERIFIED_QUEST] : 0}
                        />
                      </div>
                      <div className="w-full md:col-span-1 xl:col-span-4">
                        <SplineChart data={trafficsData[index]?.partnerData} />
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                      <div className="col-span-1 w-full h-full">
                        <ColumnsTable tableName="Traffic from CATGM to Partner" columns={partnerTrafficsColumns} data={trafficsData[index]?.partnerData} />
                      </div>
                      <div className="col-span-1 w-full h-full">
                        {trafficsData && trafficsData[index]?.partnerData && <TrafficsPartnerChart data={trafficsData[index]?.partnerData} />}
                      </div>
                    </div>
                  </>


                </Card >
                <div className="w-full flex justify-center items-center">
                  <div className="w-full max-w-[320px] h-[8px] bg-indigo-700 my-10 rounded-lg"></div>
                </div>
              </>
              )
            }
          </div>
        )) : <Card extra="p-5">
          <div className="h-screen">
            <NoData />
          </div>
        </Card>
      }
    </div >
  );
};

export default Dashboard;
